import { computed, type ComputedRef } from 'vue'
import { useRoute, useRouter, type RouteLocationNormalizedLoaded, type RouteLocationRaw } from 'vue-router'

import { getConfig } from '@/app/config'
import { AI_MODE_PATH, getHomeRoute, isAiModeSwitchEnabled } from '@/app/router/home'
import { useAuthStore } from '@/app/stores/auth'
import type { Session } from '@/app/stores/session'
import { useUiStore } from '@/app/stores/ui'

/**
 * The AI-mode switch of the navbar (docs/ai-mode/wp/AI-1.md). Its state is the page's layout: on an AI route
 * (`meta.layout: 'ai'`) the switch reads "AI", anywhere else "Classic". Entering remembers the mode for the user (auth
 * store preferences: their home becomes `/ai`) and the page they left; leaving forgets the mode and goes back to that
 * page, else home. Opening another page from AI mode (a navbar link, the profile) changes neither.
 */

/** The switch shows with the beta flag and a session only — and only while it is enabled (`router/home.ts`). */
export function aiModeSwitchVisible(state: { betaFeature: boolean; signedIn: boolean }): boolean {
  return state.betaFeature && state.signedIn
}

/** The route renders in the AI layout. */
export function isAiModeRoute(route: Pick<RouteLocationNormalizedLoaded, 'meta'>): boolean {
  return route.meta.layout === 'ai'
}

/** Where leaving AI mode goes: the page the user left when entering it, else their home without AI mode. */
export function aiModeReturnTarget(returnTo: string | null, session: Session | null): RouteLocationRaw {
  return returnTo ?? getHomeRoute(session, { aiMode: false })
}

export interface AiModeSwitch {
  /** The beta flag is on and the user is signed in. */
  available: ComputedRef<boolean>
  /** The current page is in AI mode. */
  active: ComputedRef<boolean>
  enter: () => Promise<void>
  leave: () => Promise<void>
}

export function useAiMode(): AiModeSwitch {
  const route = useRoute()
  const router = useRouter()
  const auth = useAuthStore()
  const ui = useUiStore()

  const available = computed(
    () =>
      isAiModeSwitchEnabled() &&
      aiModeSwitchVisible({ betaFeature: getConfig().betaFeature, signedIn: auth.isLoggedIn }),
  )
  const active = computed(() => isAiModeRoute(route))

  // Remembered once the navigation has landed: a refused or failed one (a chunk that does not load) changes nothing.
  async function enter(): Promise<void> {
    if (active.value) return
    const from = route.fullPath
    try {
      await router.push(AI_MODE_PATH)
    } finally {
      if (active.value) {
        ui.setAiReturnTo(from)
        auth.setAiMode(true)
      }
    }
  }

  async function leave(): Promise<void> {
    if (!active.value) return
    const target = aiModeReturnTarget(ui.aiReturnTo, auth.session)
    // Off before navigating: a target that resolves through the home rule must not come back to /ai.
    auth.setAiMode(false)
    try {
      await router.push(target)
    } finally {
      if (active.value) auth.setAiMode(true)
      else ui.setAiReturnTo(null)
    }
  }

  return { available, active, enter, leave }
}
